import { Component, computed, inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable, of, catchError } from 'rxjs';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { CompanyAccountingService } from '../../../../../services/companies/company-accounting.service';
import { CompanyAccountingDto, UpsertAccountingRequest } from '../../../../../models/api/companyAccountingDto';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { LutService } from '../../../../../services/lut.service';
import { Currency } from '../../../../../models/currency';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

export interface AmountsExpressedInOption {
  value: number;
  label: string;
}

export const AMOUNTS_EXPRESSED_IN_OPTIONS: AmountsExpressedInOption[] = [
  { value: 0, label: 'Unit' },
  { value: 1, label: 'Thousands' },
  { value: 2, label: 'Millions' }
];

type AmountField = keyof Omit<
  CompanyAccountingDto,
  'year' | 'isCarriedForward' | 'companyId' | 'currencyId' | 'amountsExpressedIn'
  | 'isFinancialsIndustry' | 'createdDate' | 'createdBy' | 'updatedDate' | 'updatedBy'
>;

interface AccountingFieldDef {
  key: AmountField;
  label: string;
  keywordKey: string;
}

@Component({
  selector: 'app-accounting-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    NumberFormatDirective,
    KeywordInputComponent,
    ReadOnlyTabDirective
  ],
  templateUrl: './accounting-tab.component.html',
  styleUrl: './accounting-tab.component.scss'
})
export class AccountingTabComponent implements OnInit, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private lutService = inject(LutService);
  private accountingService = inject(CompanyAccountingService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Accounting));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  isCarriedForward = false;
  isFinancialsIndustry = false;
  showYearWarning = false;
  showKeywords = true;
  otherMeasurement: string = '';
  otherData: CompanyAccountingDto | null = null;
  previousData: CompanyAccountingDto | null = null;

  currencies: Currency[] = [];
  amountsExpressedInOptions = AMOUNTS_EXPRESSED_IN_OPTIONS;

  readonly incomeStatementFields: AccountingFieldDef[] = [
    { key: 'sales', label: 'Sales', keywordKey: 'sales' },
    { key: 'grossIncome', label: 'Gross Income', keywordKey: 'gross' },
    { key: 'operatingIncomeEBIT', label: 'Operating Income (EBIT)', keywordKey: 'ebit' },
    { key: 'interestExpense', label: 'Interest Expense', keywordKey: 'interestExpense' },
    { key: 'incomeBeforeTaxEBT', label: 'Income before tax (EBT)', keywordKey: 'ebt' },
    { key: 'incomeTaxes', label: 'Income Taxes', keywordKey: 'incomeTaxes' },
    { key: 'netIncome', label: 'Net Income', keywordKey: 'netIncome' },
    { key: 'earningsPerShareBasicEPS', label: 'Earnings per share (Basic) EPS', keywordKey: 'eps' },
  ];

  readonly cashFlowFields: AccountingFieldDef[] = [
    { key: 'netCashFlowsFromOperatingActivities', label: 'Net Cash flows from Operating Activities', keywordKey: 'netCash' },
  ];

  readonly currentAssetsFields: AccountingFieldDef[] = [
    { key: 'totalCurrentAssets', label: 'Total Current Assets', keywordKey: 'totalCurrentAssets' },
    { key: 'totalAssets', label: 'Total Assets', keywordKey: 'totalAssets' },
  ];

  readonly liabilitiesFields: AccountingFieldDef[] = [
    { key: 'longTermDebt', label: 'Long Term Debt', keywordKey: 'longTermDebt' },
    { key: 'shortTermDebt', label: 'Short Term Debt', keywordKey: 'shortTermDebt' },
    { key: 'totalCurrentLiabilities', label: 'Total Current Liabilities', keywordKey: 'totalCurrentLiabilities' },
    { key: 'totalShareholdersEquity', label: 'Total Shareholders\' Equity', keywordKey: 'totalShareholderEquity' },
  ];

  private readonly amountFieldKeys: AmountField[] = [
    ...this.incomeStatementFields,
    ...this.cashFlowFields,
    ...this.currentAssetsFields,
    ...this.liabilitiesFields
  ].map(f => f.key);

  private readonly keywordMapping: { formField: string; type: KeywordType }[] = [
    { formField: 'sales', type: KeywordType.AccountingSales },
    { formField: 'gross', type: KeywordType.AccountingGross },
    { formField: 'ebit', type: KeywordType.AccountingEbit },
    { formField: 'interestExpense', type: KeywordType.AccountingInterestExpense },
    { formField: 'ebt', type: KeywordType.AccountingEbt },
    { formField: 'incomeTaxes', type: KeywordType.AccountingIncomeTaxes },
    { formField: 'netIncome', type: KeywordType.AccountingNetIncome },
    { formField: 'eps', type: KeywordType.AccountingEps },
    { formField: 'netCash', type: KeywordType.AccountingNetCash },
    { formField: 'totalCurrentAssets', type: KeywordType.AccountingTotalCurrentAssets },
    { formField: 'totalAssets', type: KeywordType.AccountingTotalAssets },
    { formField: 'longTermDebt', type: KeywordType.AccountingLongTermDebt },
    { formField: 'shortTermDebt', type: KeywordType.AccountingShortTermDebt },
    { formField: 'totalCurrentLiabilities', type: KeywordType.AccountingTotalCurrentLiabilities },
    { formField: 'totalShareholderEquity', type: KeywordType.AccountingTotalShareholderEquity },
  ];

  form: FormGroup = this.fb.group({
    currencyId: [''],
    amountsExpressedIn: [null as number | null],
    sales: [null as number | null],
    grossIncome: [null as number | null],
    operatingIncomeEBIT: [null as number | null],
    interestExpense: [null as number | null],
    incomeBeforeTaxEBT: [null as number | null],
    incomeTaxes: [null as number | null],
    netIncome: [null as number | null],
    earningsPerShareBasicEPS: [null as number | null],
    netCashFlowsFromOperatingActivities: [null as number | null],
    totalCurrentAssets: [null as number | null],
    totalAssets: [null as number | null],
    longTermDebt: [null as number | null],
    shortTermDebt: [null as number | null],
    totalCurrentLiabilities: [null as number | null],
    totalShareholdersEquity: [null as number | null],
    previous: this.fb.group({
      sales: [null as number | null],
      grossIncome: [null as number | null],
      operatingIncomeEBIT: [null as number | null],
      interestExpense: [null as number | null],
      incomeBeforeTaxEBT: [null as number | null],
      incomeTaxes: [null as number | null],
      netIncome: [null as number | null],
      earningsPerShareBasicEPS: [null as number | null],
      netCashFlowsFromOperatingActivities: [null as number | null],
      totalCurrentAssets: [null as number | null],
      totalAssets: [null as number | null],
      longTermDebt: [null as number | null],
      shortTermDebt: [null as number | null],
      totalCurrentLiabilities: [null as number | null],
      totalShareholdersEquity: [null as number | null],
    }),
    keywords: this.fb.group({
      sales: [''],
      gross: [''],
      ebit: [''],
      interestExpense: [''],
      ebt: [''],
      incomeTaxes: [''],
      netIncome: [''],
      eps: [''],
      netCash: [''],
      totalCurrentAssets: [''],
      totalAssets: [''],
      longTermDebt: [''],
      shortTermDebt: [''],
      totalCurrentLiabilities: [''],
      totalShareholderEquity: ['']
    })
  });

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  get previousYear(): number {
    return parseInt(this.selectedYear) - 1;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  formatNumber(value: number | null | undefined): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  getOtherValue(key: AmountField): number | null {
    return (this.otherData?.[key] as number | null | undefined) ?? null;
  }

  getOtherCurrencyName(): string {
    if (!this.otherData?.currencyId) return '';
    return this.currencies.find(c => c.id === this.otherData!.currencyId)?.name ?? '';
  }

  getOtherAmountsExpressedIn(): string {
    if (this.otherData?.amountsExpressedIn == null) return '';
    return this.amountsExpressedInOptions.find(o => o.value === this.otherData!.amountsExpressedIn)?.label ?? '';
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('accounting'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'accounting', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'accounting', year: this.selectedYear }));
    }

    this.patchKeywords();
    this.loadLookupData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const patch: Record<string, string> = {};
    for (const mapping of this.keywordMapping) {
      const kw = this.keywords.find(k => k.type === mapping.type);
      patch[mapping.formField] = kw?.value ?? '';
    }
    this.form.get('keywords')!.patchValue(patch, { emitEvent: false });
    this.form.markAsPristine();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'accounting', year }));

    if (this.otherMeasurement === year) {
      this.otherMeasurement = '';
      this.otherData = null;
    }

    this.loadData();
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;
    this.accountingService.get(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        this.otherData = resource.state === 'present' ? resource.data : null;
      },
      error: () => {
        this.otherData = null;
      }
    });
  }

  private get isCurrentYearDirty(): boolean {
    if (this.form.get('currencyId')!.dirty || this.form.get('amountsExpressedIn')!.dirty) return true;
    return this.amountFieldKeys.some(k => this.form.get(k)!.dirty);
  }

  private get isPreviousYearDirty(): boolean {
    return this.form.get('previous')!.dirty;
  }

  private get isKeywordsDirty(): boolean {
    return this.form.get('keywords')!.dirty;
  }

  private getSaveDescription(): string {
    const parts: string[] = [];
    if (this.isCurrentYearDirty) parts.push(`${this.selectedYear} accounting data`);
    if (this.isPreviousYearDirty) parts.push(`${this.previousYear} accounting data`);
    if (this.isKeywordsDirty) parts.push('keywords');
    if (parts.length === 0) return 'changes';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return parts.join(' and ');
    return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  private buildAccountingRequest(year: number, values: any, currencyId: string | null, amountsExpressedIn: number | null): UpsertAccountingRequest {
    return {
      year,
      currencyId: currencyId || null,
      amountsExpressedIn: amountsExpressedIn ?? null,
      sales: values.sales ?? null,
      grossIncome: values.grossIncome ?? null,
      operatingIncomeEBIT: values.operatingIncomeEBIT ?? null,
      interestExpense: values.interestExpense ?? null,
      incomeBeforeTaxEBT: values.incomeBeforeTaxEBT ?? null,
      incomeTaxes: values.incomeTaxes ?? null,
      netIncome: values.netIncome ?? null,
      earningsPerShareBasicEPS: values.earningsPerShareBasicEPS ?? null,
      netCashFlowsFromOperatingActivities: values.netCashFlowsFromOperatingActivities ?? null,
      totalCurrentAssets: values.totalCurrentAssets ?? null,
      totalAssets: values.totalAssets ?? null,
      longTermDebt: values.longTermDebt ?? null,
      shortTermDebt: values.shortTermDebt ?? null,
      totalCurrentLiabilities: values.totalCurrentLiabilities ?? null,
      totalShareholdersEquity: values.totalShareholdersEquity ?? null
    };
  }

  private performSave(): void {
    if (!this.companyId) return;

    const v = this.form.value;
    const currentYearDirty = this.isCurrentYearDirty || this.isCarriedForward;
    const previousYearDirty = this.isPreviousYearDirty;
    const keywordsDirty = this.isKeywordsDirty;

    if (!currentYearDirty && !previousYearDirty && !keywordsDirty) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();
    this.spinnerService.show();

    const currentCall = currentYearDirty
      ? this.accountingService.upsert(this.companyId!, this.buildAccountingRequest(
          parseInt(this.selectedYear), v, v.currencyId, v.amountsExpressedIn
        ))
      : of(null);

    const previousCall = previousYearDirty
      ? this.accountingService.upsert(this.companyId!, this.buildAccountingRequest(
          this.previousYear, v.previous,
          this.previousData?.currencyId ?? v.currencyId,
          this.previousData?.amountsExpressedIn ?? v.amountsExpressedIn
        ))
      : of(null);

    const keywordsValue = v.keywords;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, this.keywordMapping, this.keywords);
    const keywordsCall = keywordsDirty
      ? this.companyKeywordService.upsert(this.companyId!, keywordRequests)
      : of(null);

    saveTabThenKeywords([currentCall, previousCall], keywordsCall).subscribe({
      next: ([_currentResult, previousResult, keywordsResult]) => {
        this.isCarriedForward = false;
        this.showYearWarning = false;
        if (previousResult) {
          this.previousData = previousResult as CompanyAccountingDto;
        }
        if (keywordsResult) {
          this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        }
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private loadLookupData(): void {
    this.spinnerService.show();
    this.lutService.getCurrencies().subscribe({
      next: (currencies) => {
        this.currencies = currencies.sort((a, b) => a.name.localeCompare(b.name));
        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load lookup data');
        console.error('Lookup data error:', err);
      }
    });
  }

  private loadData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.show();

    const currentReq = this.accountingService.get(this.companyId, parseInt(this.selectedYear)).pipe(
      catchError(err => of({ __error: err } as any))
    );
    const previousReq = this.accountingService.get(this.companyId, this.previousYear).pipe(
      catchError(() => of(null))
    );

    forkJoin([currentReq, previousReq]).subscribe({
      next: ([currentResult, previousResult]) => {
        const previousValues = previousResult && !(previousResult as any).__error
          ? ((previousResult as any).state === 'present' ? (previousResult as any).data : null)
          : null;
        this.previousData = previousValues as CompanyAccountingDto | null;
        this.patchPreviousFromData(this.previousData);

        if (currentResult && !currentResult.__error) {
          const resource = currentResult as any; // YearlyResource<CompanyAccountingDto, CompanyAccountingContext>
          this.isFinancialsIndustry = resource.context?.isFinancialsIndustry ?? false;
          this.isCarriedForward = resource.state === 'carried-forward';
          this.showYearWarning = this.isCarriedForward || resource.state === 'absent';

          const formValues = resource.state === 'present'
            ? resource.data
            : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);

          if (formValues) {
            this.patchCurrentFromData(formValues);
          } else {
            this.resetCurrent();
          }
        } else {
          const apiError = currentResult?.__error?.error as ApiErrorResponse | undefined;
          if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
            this.isCarriedForward = false;
            this.isFinancialsIndustry = false;
            this.resetCurrent();
          } else {
            this.toast.error('Error loading accounting data');
            console.error('Accounting error:', currentResult?.__error);
          }
        }

        this.applyFinancialsLock();
        this.form.markAsPristine();
        this.spinnerService.hide();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Error loading accounting data');
        console.error('Accounting error:', err);
      }
    });
  }

  private patchCurrentFromData(data: CompanyAccountingDto): void {
    this.form.patchValue({
      currencyId: data.currencyId ?? '',
      amountsExpressedIn: data.amountsExpressedIn,
      sales: data.sales,
      grossIncome: data.grossIncome,
      operatingIncomeEBIT: data.operatingIncomeEBIT,
      interestExpense: data.interestExpense,
      incomeBeforeTaxEBT: data.incomeBeforeTaxEBT,
      incomeTaxes: data.incomeTaxes,
      netIncome: data.netIncome,
      earningsPerShareBasicEPS: data.earningsPerShareBasicEPS,
      netCashFlowsFromOperatingActivities: data.netCashFlowsFromOperatingActivities,
      totalCurrentAssets: data.totalCurrentAssets,
      totalAssets: data.totalAssets,
      longTermDebt: data.longTermDebt,
      shortTermDebt: data.shortTermDebt,
      totalCurrentLiabilities: data.totalCurrentLiabilities,
      totalShareholdersEquity: data.totalShareholdersEquity
    }, { emitEvent: false });
  }

  private patchPreviousFromData(data: CompanyAccountingDto | null): void {
    const prev = this.form.get('previous')!;
    if (!data) {
      prev.reset({
        sales: null,
        grossIncome: null,
        operatingIncomeEBIT: null,
        interestExpense: null,
        incomeBeforeTaxEBT: null,
        incomeTaxes: null,
        netIncome: null,
        earningsPerShareBasicEPS: null,
        netCashFlowsFromOperatingActivities: null,
        totalCurrentAssets: null,
        totalAssets: null,
        longTermDebt: null,
        shortTermDebt: null,
        totalCurrentLiabilities: null,
        totalShareholdersEquity: null
      }, { emitEvent: false });
      return;
    }
    prev.patchValue({
      sales: data.sales,
      grossIncome: data.grossIncome,
      operatingIncomeEBIT: data.operatingIncomeEBIT,
      interestExpense: data.interestExpense,
      incomeBeforeTaxEBT: data.incomeBeforeTaxEBT,
      incomeTaxes: data.incomeTaxes,
      netIncome: data.netIncome,
      earningsPerShareBasicEPS: data.earningsPerShareBasicEPS,
      netCashFlowsFromOperatingActivities: data.netCashFlowsFromOperatingActivities,
      totalCurrentAssets: data.totalCurrentAssets,
      totalAssets: data.totalAssets,
      longTermDebt: data.longTermDebt,
      shortTermDebt: data.shortTermDebt,
      totalCurrentLiabilities: data.totalCurrentLiabilities,
      totalShareholdersEquity: data.totalShareholdersEquity
    }, { emitEvent: false });
  }

  private applyFinancialsLock(): void {
    const lockedTopFields = ['currencyId', 'amountsExpressedIn'];
    const previous = this.form.get('previous') as FormGroup;
    // A read-only tab must stay disabled: enabling the controls here (with emitEvent: false)
    // would silently undo the lock ReadOnlyTabDirective applies to the form.
    const action: 'disable' | 'enable' = this.isFinancialsIndustry || this.isReadOnly() ? 'disable' : 'enable';

    for (const f of lockedTopFields) {
      this.form.get(f)?.[action]({ emitEvent: false });
    }
    for (const key of this.amountFieldKeys) {
      this.form.get(key)?.[action]({ emitEvent: false });
      previous.get(key)?.[action]({ emitEvent: false });
    }
    this.form.get('keywords')?.[action]({ emitEvent: false });
  }

  private resetCurrent(): void {
    this.form.patchValue({
      currencyId: '',
      amountsExpressedIn: null,
      sales: null,
      grossIncome: null,
      operatingIncomeEBIT: null,
      interestExpense: null,
      incomeBeforeTaxEBT: null,
      incomeTaxes: null,
      netIncome: null,
      earningsPerShareBasicEPS: null,
      netCashFlowsFromOperatingActivities: null,
      totalCurrentAssets: null,
      totalAssets: null,
      longTermDebt: null,
      shortTermDebt: null,
      totalCurrentLiabilities: null,
      totalShareholdersEquity: null
    }, { emitEvent: false });
  }
}
